import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { RISK_CATEGORIES, PIE_COLORS } from '../../utils/constants';
import { Shield, TrendingUp, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FACTOR_COLORS = {
  questionnaireScore: '#3b82f6',
  ageScore: '#22c55e',
  incomeScore: '#f59e0b',
  expenseScore: '#ef4444',
  horizonScore: '#8b5cf6',
};

const FACTOR_LABELS = {
  questionnaireScore: 'Your Preferences',
  ageScore: 'Age',
  incomeScore: 'Income Level',
  expenseScore: 'Spending Pattern',
  horizonScore: 'Investment Horizon',
};

const RiskResult = ({ result }) => {
  const navigate = useNavigate();
  const { riskScore, riskCategory, recommendedAllocation, breakdown } = result;
  const categoryInfo = RISK_CATEGORIES[riskCategory] || RISK_CATEGORIES.MODERATE;

  const pieData = Object.entries(recommendedAllocation || {})
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value,
    }));

  return (
    <div className="space-y-6">
      {/* Score Card */}
      <div className="card text-center">
        <Shield className="h-12 w-12 mx-auto mb-3" style={{ color: categoryInfo.color }} />
        <h3 className="text-3xl font-bold text-gray-900">{riskScore}/100</h3>
        <div className="flex items-center justify-center gap-2 mt-2">
          <span className="text-2xl">{categoryInfo.emoji}</span>
          <span className="text-xl font-semibold" style={{ color: categoryInfo.color }}>
            {categoryInfo.label}
          </span>
        </div>
        <p className="text-gray-500 mt-1">{categoryInfo.description}</p>

        <div className="mt-4 w-full bg-gray-200 rounded-full h-3">
          <div
            className="h-3 rounded-full transition-all duration-1000"
            style={{ width: `${riskScore}%`, backgroundColor: categoryInfo.color }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>Conservative</span>
          <span>Moderate</span>
          <span>Aggressive</span>
        </div>
      </div>

      {/* Score Breakdown */}
      {breakdown && (
        <div className="card">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Score Breakdown</h4>
          <div className="space-y-3">
            {Object.entries(breakdown).map(([key, value]) => (
              <div key={key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-gray-700">{FACTOR_LABELS[key] || key}</span>
                  <span className="font-semibold" style={{ color: FACTOR_COLORS[key] }}>{value}/100</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="h-2.5 rounded-full transition-all duration-700"
                    style={{ width: `${value}%`, backgroundColor: FACTOR_COLORS[key] }}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Your overall risk score: {riskScore}/100
          </p>
        </div>
      )}

      {/* Asset Allocation Pie */}
      {pieData.length > 0 && (
        <div className="card">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary-600" />
            Recommended Asset Allocation
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="value"
                  label={({ name, value }) => `${name} ${value}%`}
                >
                  {pieData.map((_, index) => (
                    <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <button
        onClick={() => navigate('/portfolio')}
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        View Optimized Portfolio <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
};

export default RiskResult;
